import type { ItemStockSummary, StockMovement, StockMovementList } from '@erp/shared';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Loader2, RotateCcw, Scale, Trash2, Truck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState } from '../../../components/ui/index.js';
import { api } from '../../../lib/api.js';
import { cn } from '../../../lib/cn.js';
import { formatDateTime, formatNumber } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';
import { movementRemovable } from '../ItemStockTab.js';

import { dayBoundIso, fmtQty, localDayStartIso, referencePath } from './profileFormat.js';
import { BranchStockCards, LoadError, QtyText } from './ProfileParts.js';

type TypeFilter = '' | 'in' | 'out' | StockMovement['movementType'];
type Range = '7' | '30' | '90' | '365' | 'all' | 'custom';

const PAGE = 200;
const TYPE_TONE: Record<StockMovement['movementType'], 'success' | 'warning' | 'info' | 'danger'> = {
  receipt: 'success',
  shipment: 'danger',
  adjustment: 'info',
  transfer_in: 'success',
  transfer_out: 'warning',
};
const TYPE_ICON: Record<StockMovement['movementType'], typeof Truck> = {
  receipt: ArrowDownLeft,
  shipment: ArrowUpRight,
  adjustment: Scale,
  transfer_in: ArrowLeftRight,
  transfer_out: ArrowLeftRight,
};
const selectCls = 'h-9 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted outline-none focus:border-brand';
const dateCls = 'h-9 rounded-full border border-border bg-bg px-3 text-xs text-fg outline-none focus:border-brand';

interface Props {
  itemId: string;
  baseUnit: string;
  pack: { name: string; factor: number } | null;
  summary: ItemStockSummary | null;
  canCost: boolean;
  canAdjust: boolean;
  /** Something changed stock (a manual adjustment deleted) — refresh the page's figures. */
  onChanged: () => void;
  /** Bumped by the page after it changes stock itself (an adjustment) — reload. */
  refreshKey: number;
  /** The branch filter, owned by the page so other tabs can open this one on a branch ('' = all). */
  warehouseId: string;
  onWarehouseChange: (warehouseId: string) => void;
}

/**
 * The product's stock card — the Stock movements page for ONE product, and more:
 * where each branch stands (the cards on top double as a branch filter), what
 * moved in and out, the document behind each move and who made it, and the
 * balance after every line, the way a paper stock card reads.
 *
 * The balance is computed by the server against what is on hand now, over the
 * same branches as the list, so filtering by type or date never changes it.
 */
export function ItemMovementsTab({
  itemId, baseUnit, pack, summary, canCost, canAdjust, onChanged, refreshKey, warehouseId, onWarehouseChange: setWarehouseId,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const { t: ti } = useTranslation('inventory');
  const locale = i18n.language;
  const [type, setType] = useState<TypeFilter>('');
  const [range, setRange] = useState<Range>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [list, setList] = useState<StockMovementList | null>(null);
  const [error, setError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { itemId };
    if (warehouseId) p.warehouseId = warehouseId;
    if (type) p.type = type;
    if (range === 'custom') {
      const f = dayBoundIso(from, 'start');
      const e = dayBoundIso(to, 'end');
      if (f) p.from = f;
      if (e) p.to = e;
    } else if (range !== 'all') {
      p.from = localDayStartIso(Number(range) - 1);
    }
    return p;
  }, [itemId, warehouseId, type, range, from, to]);

  // Clicking through branch cards fires requests faster than they return; only
  // the newest one may land, or the list shows one branch under another's filter.
  const seq = useRef(0);
  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    setError(false);
    try {
      const r = await api().get<StockMovementList>('/stock/movements', { params: { ...params, limit: PAGE, offset: 0 } });
      if (mine === seq.current) setList(r.data);
    } catch {
      if (mine === seq.current) setError(true);
    }
  }, [params]);

  useEffect(() => {
    setList(null);
    void load();
  }, [load, refreshKey]);

  const loadMore = async (): Promise<void> => {
    if (!list) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const r = await api().get<StockMovementList>('/stock/movements', { params: { ...params, limit: PAGE, offset: list.items.length } });
      if (mine === seq.current) setList({ ...r.data, items: [...list.items, ...r.data.items] });
    } catch {
      toast.error(t('detail.loadError', { defaultValue: 'Could not load.' }));
    } finally {
      setLoadingMore(false);
    }
  };

  // Delete a manual adjustment: the server reverses its quantity and takes its
  // value back out of the average, then drops the row. Destructive — confirm.
  const onRemove = async (m: StockMovement): Promise<void> => {
    const confirmed = await confirmDialog({
      title: ti('movements.remove.title'),
      message: ti('movements.remove.confirm', { quantity: fmtQty(m.quantity, locale), warehouse: m.warehouseName }),
      tone: 'danger',
      confirmLabel: ti('movements.remove.action'),
    });
    if (!confirmed) return;
    try {
      await api().delete(`/stock/movements/${m.id}`);
      toast.success(ti('movements.remove.done'));
      await load();
      onChanged();
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      if (code === 'stock.insufficient') toast.error(ti('movements.remove.errors.insufficient'));
      else if (code === 'movement.not_removable') toast.error(ti('movements.remove.errors.notRemovable'));
      else toast.error(ti('movements.remove.errors.failed'));
    }
  };

  const refLabel = (m: StockMovement): string => {
    if (m.referenceType) return t(`profile.ref.${m.referenceType}`, { defaultValue: m.referenceType });
    if (m.movementType === 'adjustment') return t('profile.ref.manual', { defaultValue: 'Manual adjustment' });
    return '';
  };

  const columns: DataTableColumn<StockMovement>[] = [
    {
      id: 'occurredAt',
      header: ti('movements.columns.occurredAt'),
      sortValue: (r) => r.occurredAt,
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (r) => formatDateTime(r.occurredAt, { locale }),
    },
    {
      id: 'type',
      header: ti('movements.columns.type'),
      sortValue: (r) => ti(`movements.type.${r.movementType}`),
      searchValue: (r) => ti(`movements.type.${r.movementType}`),
      cell: (r) => {
        const Icon = TYPE_ICON[r.movementType];
        // A returned line is a receipt; an emptied level an adjustment — the sign
        // is what moved, so it colours the badge, not the label.
        const tone = r.movementType === 'adjustment' ? (Number(r.quantity) < 0 ? 'warning' : 'info') : TYPE_TONE[r.movementType];
        return (
          <Badge variant={tone} className="whitespace-nowrap">
            <Icon className="h-3 w-3" aria-hidden />
            {ti(`movements.type.${r.movementType}`)}
          </Badge>
        );
      },
    },
    {
      id: 'warehouse',
      header: ti('movements.columns.warehouse'),
      sortValue: (r) => r.warehouseName,
      searchValue: (r) => `${r.warehouseCode} ${r.warehouseName}`,
      cell: (r) => (
        <span className="whitespace-nowrap">
          {r.warehouseName}
          <span className="ms-1 font-mono text-[11px] text-fg-subtle">{r.warehouseCode}</span>
        </span>
      ),
    },
    {
      id: 'reference',
      header: t('profile.movements.reference', { defaultValue: 'Document' }),
      sortValue: (r) => r.referenceNumber ?? refLabel(r),
      searchValue: (r) => `${r.referenceNumber ?? ''} ${refLabel(r)}`,
      cell: (r) => {
        const path = referencePath(r.referenceType, r.referenceId);
        const label = refLabel(r);
        return (
          <span className="flex flex-col leading-tight">
            {r.referenceNumber ? (
              path ? (
                <Link to={path} className="font-mono text-xs font-semibold text-brand hover:underline">{r.referenceNumber}</Link>
              ) : (
                <span className="font-mono text-xs font-semibold">{r.referenceNumber}</span>
              )
            ) : null}
            {label ? <span className="text-[11px] text-fg-muted">{label}</span> : (!r.referenceNumber ? <span className="text-fg-subtle">—</span> : null)}
          </span>
        );
      },
    },
    {
      id: 'in',
      header: t('profile.movements.in', { defaultValue: 'In' }),
      align: 'end',
      sortValue: (r) => (Number(r.quantity) > 0 ? Number(r.quantity) : null),
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => (Number(r.quantity) > 0
        ? <span dir="ltr" className="font-semibold text-success">+{fmtQty(r.quantity, locale)}</span>
        : <span className="text-fg-subtle">—</span>),
    },
    {
      id: 'out',
      header: t('profile.movements.out', { defaultValue: 'Out' }),
      align: 'end',
      sortValue: (r) => (Number(r.quantity) < 0 ? -Number(r.quantity) : null),
      cellClassName: 'font-mono tabular-nums',
      cell: (r) => (Number(r.quantity) < 0
        ? <span dir="ltr" className="font-semibold text-danger">−{fmtQty(Math.abs(Number(r.quantity)), locale)}</span>
        : <span className="text-fg-subtle">—</span>),
    },
    {
      id: 'balance',
      header: t('profile.movements.balance', { defaultValue: 'Balance' }),
      align: 'end',
      sortValue: (r) => (r.balanceAfter == null ? null : Number(r.balanceAfter)),
      cell: (r) => (r.balanceAfter == null ? '—' : <QtyText value={r.balanceAfter} strong />),
    },
  ];
  if (canCost) {
    columns.push({
      id: 'unitCost',
      header: ti('movements.columns.unitCost'),
      align: 'end',
      sortValue: (r) => (r.unitCost && r.unitCostCurrencyCode ? Number(r.unitCost) : null),
      cellClassName: 'whitespace-nowrap font-mono tabular-nums text-fg-muted',
      cell: (r) => (r.unitCost && r.unitCostCurrencyCode && Number(r.unitCost) !== 0
        ? formatMoney(r.unitCost, { locale, currency: r.unitCostCurrencyCode })
        : '—'),
    });
  }
  columns.push(
    {
      id: 'actor',
      header: t('profile.movements.actor', { defaultValue: 'By' }),
      sortValue: (r) => r.actorName ?? '',
      searchValue: (r) => r.actorName ?? '',
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (r) => r.actorName ?? t('profile.system', { defaultValue: 'System' }),
    },
    {
      id: 'note',
      header: ti('movements.columns.note'),
      searchValue: (r) => r.note ?? '',
      cellClassName: 'max-w-[16rem] truncate text-fg-muted',
      cell: (r) => <span title={r.note ?? undefined}>{r.note ?? '—'}</span>,
    },
  );
  if (canAdjust) {
    columns.push({
      id: 'actions',
      header: '',
      align: 'end',
      cell: (r) => (movementRemovable(r) ? (
        <Button type="button" variant="ghost" size="sm" onClick={() => void onRemove(r)} aria-label={ti('movements.remove.action')} title={ti('movements.remove.action')}>
          <Trash2 className="h-4 w-4 text-danger" aria-hidden />
        </Button>
      ) : null),
    });
  }

  const anyFilter = Boolean(warehouseId || type || range !== 'all');
  const reset = (): void => { setWarehouseId(''); setType(''); setRange('all'); setFrom(''); setTo(''); };
  const ranges: Array<{ id: Range; label: string }> = [
    { id: '7', label: t('profile.range.d7', { defaultValue: '7 days' }) },
    { id: '30', label: t('profile.range.d30', { defaultValue: '30 days' }) },
    { id: '90', label: t('profile.range.d90', { defaultValue: '90 days' }) },
    { id: '365', label: t('profile.range.year', { defaultValue: '1 year' }) },
    { id: 'all', label: t('profile.range.all', { defaultValue: 'All time' }) },
    { id: 'custom', label: t('profile.range.custom', { defaultValue: 'Custom' }) },
  ];

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className={selectCls} aria-label={ti('movements.columns.warehouse')}>
        <option value="">{t('profile.stock.allBranches', { defaultValue: 'All branches' })}</option>
        {(summary?.warehouses ?? []).map((w) => <option key={w.warehouseId} value={w.warehouseId}>{w.name}</option>)}
      </select>
      <select value={type} onChange={(e) => setType(e.target.value as TypeFilter)} className={selectCls} aria-label={ti('movements.columns.type')}>
        <option value="">{t('profile.movements.anyType', { defaultValue: 'Every movement' })}</option>
        <option value="in">{t('profile.movements.onlyIn', { defaultValue: 'Everything in' })}</option>
        <option value="out">{t('profile.movements.onlyOut', { defaultValue: 'Everything out' })}</option>
        {(['receipt', 'shipment', 'adjustment', 'transfer_in', 'transfer_out'] as const).map((k) => (
          <option key={k} value={k}>{ti(`movements.type.${k}`)}</option>
        ))}
      </select>
      <select value={range} onChange={(e) => setRange(e.target.value as Range)} className={selectCls} aria-label={t('profile.range.label', { defaultValue: 'Period' })}>
        {ranges.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
      </select>
      {range === 'custom' && (
        <>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={dateCls} aria-label={t('profile.range.from', { defaultValue: 'From' })} />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={dateCls} aria-label={t('profile.range.to', { defaultValue: 'To' })} />
        </>
      )}
      {anyFilter && (
        <Button variant="ghost" size="sm" className="h-8 rounded-full" onClick={reset}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />{t('profile.movements.clear', { defaultValue: 'Clear' })}
        </Button>
      )}
    </div>
  );

  const nf = (n: number): string => formatNumber(n, { locale });
  const net = Number(list?.totals.net ?? 0);

  return (
    <div className="flex flex-col gap-4">
      {summary && (
        <BranchStockCards
          summary={summary}
          baseUnit={baseUnit}
          pack={pack}
          canCost={canCost}
          selected={warehouseId}
          onSelect={(id) => setWarehouseId(id)}
        />
      )}

      {/* What the filtered list adds up to — over EVERY matching movement, not the page. */}
      {list && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Figure label={t('profile.movements.count', { defaultValue: 'Movements' })} value={nf(list.total)} />
          <Figure label={t('profile.movements.totalIn', { defaultValue: 'Total in' })} value={`+${fmtQty(list.totals.in, locale)}`} tone="success" />
          <Figure label={t('profile.movements.totalOut', { defaultValue: 'Total out' })} value={`−${fmtQty(list.totals.out, locale)}`} tone="danger" />
          <Figure
            label={t('profile.movements.net', { defaultValue: 'Net change' })}
            value={`${net > 0 ? '+' : net < 0 ? '−' : ''}${fmtQty(Math.abs(net), locale)}`}
            tone={net < 0 ? 'danger' : net > 0 ? 'success' : undefined}
          />
          <Figure
            label={warehouseId
              ? t('profile.movements.onHandBranch', { defaultValue: 'On hand in this branch' })
              : t('profile.movements.onHandNow', { defaultValue: 'On hand now' })}
            value={fmtQty(list.onHand ?? 0, locale)}
            tone={Number(list.onHand ?? 0) < 0 ? 'danger' : undefined}
            unit={baseUnit}
          />
        </div>
      )}

      {error ? (
        <LoadError />
      ) : !list ? (
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      ) : (
        <Card className="overflow-hidden p-0">
          {list.items.length === 0 ? (
            <div className="flex flex-col gap-3 p-4">
              {toolbar}
              <EmptyState
                icon={<Truck className="h-8 w-8" aria-hidden />}
                title={anyFilter
                  ? t('profile.movements.noneFiltered', { defaultValue: 'No movements match these filters' })
                  : t('detail.noMovements', { defaultValue: 'No stock movements' })}
                description={anyFilter ? undefined : ti('movements.emptyDescription')}
              />
            </div>
          ) : (
            <>
              <DataTable
                columns={columns}
                data={list.items}
                rowKey={(r) => r.id}
                pageSize={25}
                pageSizeOptions={[25, 50, 100, 200]}
                toolbar={toolbar}
                rowClassName={(r) => (r.balanceAfter != null && Number(r.balanceAfter) < 0 ? 'bg-bg-subtle' : undefined)}
                searchPlaceholder={t('profile.movements.search', { defaultValue: 'Search document, person, note…' })}
                footerRows={[{
                  key: 'totals',
                  className: 'font-semibold',
                  cells: {
                    occurredAt: t('profile.movements.footer', { defaultValue: 'Totals — all {{n}} matching', n: nf(list.total) }),
                    in: <span dir="ltr" className="font-mono tabular-nums text-success">+{fmtQty(list.totals.in, locale)}</span>,
                    out: <span dir="ltr" className="font-mono tabular-nums text-danger">−{fmtQty(list.totals.out, locale)}</span>,
                    balance: list.onHand != null ? <QtyText value={list.onHand} strong /> : null,
                  },
                }]}
              />
              {list.items.length < list.total && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-fg-muted">
                  <span>
                    {t('profile.movements.loaded', {
                      defaultValue: 'Showing the latest {{shown}} of {{total}}',
                      shown: nf(list.items.length),
                      total: nf(list.total),
                    })}
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                    {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                    {t('profile.movements.loadMore', { defaultValue: 'Load older movements' })}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function Figure({ label, value, tone, unit }: { label: string; value: string; tone?: 'success' | 'danger'; unit?: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-border bg-bg-subtle px-3 py-2.5">
      <div className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className={cn('mt-1 font-mono text-lg font-bold tabular-nums', tone === 'success' && 'text-success', tone === 'danger' && 'text-danger')}>
        <span dir="ltr">{value}</span>
        {unit && <span className="ms-1 font-sans text-xs font-normal text-fg-muted">{unit}</span>}
      </div>
    </div>
  );
}
